import type { RequestedParameterIntentInput } from '../../../shared/api/grpc/requestedParameterIntent';
import { getEquivalentRevitParameterNames } from '../../../shared/lib/revitParameterNames';
import type { MappingParameterScopeRequirement } from './mappingReadinessFamilyExclusions';

export { buildMappingFamilyRequirementAssessmentInputs } from '../../../shared/lib/mappingParameterScopeRequirements';

export interface MappingReadinessSnapshotRequest {
  parameterNames: string[];
  requestedParameters: RequestedParameterIntentInput[];
  categoryNames: string[];
}

function appendIntent(
  target: Map<string, RequestedParameterIntentInput>,
  parameterName: string,
  destination: 'project' | 'family',
  bindingKind: 'type' | 'instance'
): void {
  getEquivalentRevitParameterNames(parameterName).forEach((name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const key = `${trimmed.toLowerCase()}::${destination}::${bindingKind}`;
    target.set(key, { name: trimmed, destination, bindingKind });
  });
}

export function buildMappingReadinessSnapshotRequest(
  requirements: MappingParameterScopeRequirement[]
): MappingReadinessSnapshotRequest {
  const intents = new Map<string, RequestedParameterIntentInput>();
  let requiresObjectTypeClassification = false;
  const familyCategories = new Set<string>();

  requirements.forEach((requirement) => {
    appendIntent(
      intents,
      requirement.parameterName,
      requirement.destination,
      requirement.bindingKind
    );
    if (requirement.target.kind === 'editableFamilyCategories') {
      requirement.target.categories.forEach((category) => {
        const trimmed = category.trim();
        if (trimmed) familyCategories.add(trimmed);
      });
    } else if (requirement.target.kind === 'familyObjectTypes') {
      requiresObjectTypeClassification = true;
      appendIntent(
        intents,
        requirement.target.discriminatorParameterName,
        requirement.target.discriminatorDestination,
        requirement.target.discriminatorBindingKind
      );
    }
  });

  const requestedParameters = Array.from(intents.values()).sort((left, right) =>
    `${left.name}:${left.destination}:${left.bindingKind}`.localeCompare(
      `${right.name}:${right.destination}:${right.bindingKind}`
    )
  );
  return {
    parameterNames: Array.from(new Set(requestedParameters.map((intent) => intent.name)))
      .sort((left, right) => left.localeCompare(right)),
    requestedParameters,
    categoryNames: requiresObjectTypeClassification
      ? []
      : Array.from(familyCategories).sort((left, right) => left.localeCompare(right)),
  };
}
